import React, { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import CohortBuilder from './CohortBuilder';
import YearLevelBadge from '../components/YearLevelBadge';

const YEAR_OPTIONS = ['Year 1', 'Year 2'];

function CohortCard({ cohort, students, onEdit, onView }) {
  const enrolled = students.filter(s => cohort.studentIds.map(String).includes(String(s.id)));
  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--neutral-200)',
      borderRadius: '10px',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '2px' }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: 'var(--neutral-900)' }}>{cohort.name}</h3>
            <YearLevelBadge levelYear={cohort.levelYear} />
          </div>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>{enrolled.length} students</p>
        </div>
        <span className={`status-pill ${cohort.status?.toLowerCase() || 'active'}`} style={{ fontSize: '11px' }}>
          {cohort.status || 'Active'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={() => onView(cohort)}
          style={{ flex: 1, padding: '6px', fontSize: '12px', background: 'var(--neutral-100)', border: '1px solid var(--neutral-200)', borderRadius: '6px', cursor: 'pointer', color: 'var(--neutral-700)' }}
        >
          View
        </button>
        <button
          onClick={() => onEdit(cohort)}
          style={{ flex: 1, padding: '6px', fontSize: '12px', background: 'var(--neutral-100)', border: '1px solid var(--neutral-200)', borderRadius: '6px', cursor: 'pointer', color: 'var(--neutral-700)' }}
        >
          Edit
        </button>
      </div>
    </div>
  );
}

function GroupCard({ group, cohorts, onEdit }) {
  const groupCohorts = cohorts.filter(c => group.cohortIds.includes(c.id));
  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--neutral-200)',
      borderRadius: '10px',
      padding: '16px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div>
          <h3 style={{ margin: '0 0 2px 0', fontSize: '14px', fontWeight: '600', color: 'var(--neutral-900)' }}>{group.name}</h3>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>{group.year} · {groupCohorts.length} cohorts</p>
        </div>
        <button
          onClick={() => onEdit(group)}
          style={{ padding: '4px 10px', fontSize: '12px', background: 'var(--neutral-100)', border: '1px solid var(--neutral-200)', borderRadius: '6px', cursor: 'pointer', color: 'var(--neutral-700)' }}
        >
          Edit
        </button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {groupCohorts.map(c => (
          <span key={c.id} style={{
            background: 'var(--neutral-100)',
            border: '1px solid var(--neutral-200)',
            borderRadius: '4px',
            padding: '3px 8px',
            fontSize: '11px',
            color: 'var(--neutral-700)'
          }}>
            {c.name}
          </span>
        ))}
        {groupCohorts.length === 0 && <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No cohorts assigned</span>}
      </div>
    </div>
  );
}

export default function SchoolView({
  school,
  allStudents,
  schoolCohorts,
  schoolGroups,
  sessions,
  allCohorts,
  auditors,
  onBack,
  onCohortView,
  onGroupSave,
  onCohortSave,
  onCohortsReplace,
  onPlanSession,
  syncStatus,
  onSyncStudents,
  onSessionClick,
  onStudentClick,
}) {
  const [tab, setTab] = useState('roster');
  const [rosterQuery, setRosterQuery] = useState('');
  const [showCohortBuilder, setShowCohortBuilder] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [localSyncedAt, setLocalSyncedAt] = useState(syncStatus?.synced_at || null);
  const [localStale, setLocalStale] = useState(syncStatus?.stale ?? true);

  useEffect(() => {
    if (syncStatus) {
      setLocalSyncedAt(syncStatus.synced_at || null);
      setLocalStale(syncStatus.stale ?? true);
    }
  }, [syncStatus]);

  // Group form state
  const [groupName, setGroupName] = useState('');
  const [groupYear, setGroupYear] = useState('Year 1');
  const [groupCohortIds, setGroupCohortIds] = useState([]);

  const schoolStudents = useMemo(() =>
    allStudents.filter(s => String(s.schoolId) === String(school.id)),
    [allStudents, school.id]
  );

  const assignedStudentIds = useMemo(() =>
    new Set(schoolCohorts.flatMap(c => (c.studentIds || []).map(String))),
    [schoolCohorts]
  );

  const unassignedCount = schoolStudents.filter(s => !assignedStudentIds.has(String(s.id))).length;

  const filteredRoster = useMemo(() => {
    const q = rosterQuery.trim().toLowerCase();
    if (!q) return schoolStudents;
    return schoolStudents.filter(s =>
      s.name.toLowerCase().includes(q) ||
      s.grade.toLowerCase().includes(q)
    );
  }, [schoolStudents, rosterQuery]);

  function openGroupCreate() {
    setEditingGroup(null);
    setGroupName('');
    setGroupYear('Year 1');
    setGroupCohortIds([]);
    setShowGroupForm(true);
  }

  function openGroupEdit(group) {
    setEditingGroup(group);
    setGroupName(group.name);
    setGroupYear(group.year);
    setGroupCohortIds([...(group.cohortIds || [])]);
    setShowGroupForm(true);
  }

  function saveGroup() {
    if (!groupName.trim()) return;
    const groupData = {
      id: editingGroup ? editingGroup.id : 'group-' + Date.now(),
      name: groupName.trim(),
      schoolId: school.id,
      year: groupYear,
      cohortIds: groupCohortIds,
    };
    onGroupSave(groupData, !!editingGroup);
    setShowGroupForm(false);
  }

  function toggleGroupCohort(id) {
    setGroupCohortIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  return (
    <div className="animate-fade-in">
      <button className="btn-back-link" onClick={onBack}>← Back to Schools</button>

      <div className="detail-header-card" style={{ marginBottom: '20px' }}>
        <div className="detail-header-left">
          <h1 className="detail-cohort-title">{school.school_name || school.name}</h1>
          {(school.branch_name || school.branchName || school.branch_id) && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              marginTop: '6px',
              marginBottom: '4px',
              padding: '4px 10px',
              borderRadius: '999px',
              background: '#e8f0fe',
              border: '1px solid #c5d7f5',
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--primary-navy, #0147a2)',
            }}>
              <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Branch
              </span>
              <span>{school.branch_name || school.branchName || `Branch #${school.branch_id}`}</span>
            </div>
          )}
          <div className="detail-cohort-meta-row">
            <span>{[school.city, school.state].filter(Boolean).join(', ')}</span>
            <span><strong>{schoolStudents.length}</strong> students enrolled</span>
            {unassignedCount > 0 && <span style={{ color: '#b45309' }}><strong>{unassignedCount}</strong> unassigned</span>}
            <span style={{ color: localStale ? '#b45309' : 'var(--text-muted)', fontSize: '12px' }}>
              {localSyncedAt
                ? <>Last synced: {new Date(localSyncedAt).toLocaleString()}{localStale ? ' — sync recommended' : ''}</>
                : 'Never synced'}
            </span>
          </div>
        </div>
        <div className="detail-header-right" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {onSyncStudents && (
            <button
              className="btn-create-cohort"
              style={{ background: localStale ? 'var(--warning-600, #d97706)' : undefined }}
              disabled={syncing}
              onClick={async () => {
                setSyncing(true);
                try {
                  await onSyncStudents();
                } catch (e) {
                  // parent shows toast on error
                } finally {
                  setSyncing(false);
                }
              }}
            >
              {syncing ? 'Syncing…' : '↺ Refresh Roster'}
            </button>
          )}
          <button className="btn-create-cohort" onClick={() => setShowCohortBuilder(true)}>🧩 Cohort Builder</button>
          <span title={schoolCohorts.length < 2 ? 'Create at least 2 cohorts first' : undefined}>
            <button
              className="btn-create-cohort"
              onClick={openGroupCreate}
              disabled={schoolCohorts.length < 2}
              style={{ background: schoolCohorts.length < 2 ? 'var(--neutral-400)' : 'var(--neutral-700)', cursor: schoolCohorts.length < 2 ? 'not-allowed' : 'pointer', pointerEvents: schoolCohorts.length < 2 ? 'none' : undefined }}
            >+ Create Group</button>
          </span>
        </div>
      </div>

      <div className="tabs-navigation-row">
        {[
          { id: 'roster', label: `All Students (${schoolStudents.length})` },
          { id: 'cohorts', label: `Cohorts (${schoolCohorts.length})` },
          { id: 'groups', label: `Groups (${schoolGroups.length})` },
          { id: 'sessions', label: `Sessions (${(sessions || []).filter(s => {
            if (String(s.schoolId) === String(school.id)) return true;
            return (schoolCohorts || []).some(c => String(c.id) === String(s.cohortId));
          }).length})` },
        ].map(t => (
          <button
            key={t.id}
            className={`tab-nav-button ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'roster' && (
        <div className="tab-panel-container">
          <div style={{ marginBottom: '12px' }}>
            <input
              type="text"
              placeholder="Search students…"
              value={rosterQuery}
              onChange={e => setRosterQuery(e.target.value)}
              style={{ padding: '8px 12px', fontSize: '13px', border: '1px solid var(--neutral-300)', borderRadius: '7px', width: '280px', outline: 'none', background: 'var(--surface)', color: 'var(--neutral-900)' }}
            />
          </div>
          <div className="students-table-card">
            <table className="students-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Student Name</th>
                  <th>Grade</th>
                  <th>Cohort</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoster.map((s, i) => {
                  const cohort = schoolCohorts.find(c => c.studentIds?.map(String).includes(String(s.id)));
                  return (
                    <tr key={s.id}>
                      <td style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{i + 1}</td>
                      <td>
                        <button
                          onClick={() => onStudentClick && onStudentClick(s)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: 'var(--primary-navy, #0147a2)',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontWeight: 600
                          }}
                        >
                          {s.name}
                        </button>
                      </td>
                      <td>{s.grade}</td>
                      <td>
                        {cohort
                          ? <span style={{ fontSize: '11px', background: 'var(--neutral-100)', border: '1px solid var(--neutral-200)', borderRadius: '4px', padding: '2px 6px', color: 'var(--neutral-700)' }}>{cohort.name}</span>
                          : <span style={{ fontSize: '11px', color: '#b45309', fontStyle: 'italic' }}>Unassigned</span>
                        }
                      </td>
                    </tr>
                  );
                })}
                {filteredRoster.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No students match your search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'cohorts' && (
        <div className="tab-panel-container">
          {schoolCohorts.length === 0 ? (
            <div className="empty-state-card" style={{ padding: '40px 24px', borderStyle: 'solid' }}>
              <div className="empty-state-icon-wrapper">👥</div>
              <h3 className="empty-state-heading">No cohorts yet</h3>
              <p className="empty-state-description">Use the Cohort Builder to create and assign students to cohorts.</p>
              <button className="btn-create-cohort" style={{ padding: '8px 18px', fontSize: '13px' }} onClick={() => setShowCohortBuilder(true)}>🧩 Open Cohort Builder</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' }}>
              {schoolCohorts.map(c => (
                <CohortCard
                  key={c.id}
                  cohort={c}
                  students={allStudents}
                  onEdit={() => setShowCohortBuilder(true)}
                  onView={onCohortView}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'groups' && (
        <div className="tab-panel-container">
          {schoolGroups.length === 0 ? (
            <div className="empty-state-card" style={{ padding: '40px 24px', borderStyle: 'solid' }}>
              <div className="empty-state-icon-wrapper">🗂️</div>
              <h3 className="empty-state-heading">No groups yet</h3>
              <p className="empty-state-description">Groups combine cohorts that run through the same council sequence together.</p>
              {schoolCohorts.length < 2
                ? <p style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>Create at least 2 cohorts before making a group.</p>
                : <button className="btn-create-cohort" style={{ padding: '8px 18px', fontSize: '13px' }} onClick={openGroupCreate}>+ Create Group</button>
              }
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
              {schoolGroups.map(g => (
                <GroupCard key={g.id} group={g} cohorts={schoolCohorts} onEdit={openGroupEdit} />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'sessions' && (() => {
        const sessionsForSchool = (sessions || []).filter(s => {
          if (String(s.schoolId) === String(school.id)) return true;
          return (schoolCohorts || []).some(c => String(c.id) === String(s.cohortId));
        });
        return (
        <div className="tab-panel-container">
          <div className="dashboard-title-row" style={{ marginBottom: '16px' }}>
            <div>
              <h1 className="dashboard-title">Sessions</h1>
              <p className="dashboard-subtitle">Planned, live, and completed Glosco audit sessions for this school.</p>
            </div>
            {onPlanSession && (
              <button className="btn-create-cohort" onClick={() => onPlanSession(school.id)}>
                + Plan Session
              </button>
            )}
          </div>

          {sessionsForSchool.length === 0 ? (
            <div className="empty-state-card" style={{ padding: '40px 24px', borderStyle: 'solid' }}>
              <div className="empty-state-icon-wrapper">📅</div>
              <h3 className="empty-state-heading">No sessions yet</h3>
              <p className="empty-state-description">Plan a session for this school's cohorts to get started.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '14px' }}>
              {sessionsForSchool.map(session => {
                const cohort = allCohorts.find(c => String(c.id) === String(session.cohortId));
                return (
                  <div
                    key={session.id}
                    className="timeline-session-card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSessionClick && onSessionClick(session)}
                  >
                    <div className="session-card-header">
                      <div>
                        <span className="session-card-type">
                          {session.status === 'Completed' && '✓ '}
                          {session.sessionType}
                        </span>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                          {(session.scheduledAt || '').split(/[T ]/)[0]}
                        </div>
                      </div>
                      <span className={`status-pill ${(session.status || '').toLowerCase()}`}>{session.status}</span>
                    </div>

                    <div className="session-card-meta">
                      <div>🏫 School: <strong>{session.schoolName || school.school_name || school.name}</strong></div>
                      <div style={{ marginTop: '2px' }}>👥 Cohort: <strong>{session.cohortName || cohort?.name || '—'}</strong></div>
                      <div style={{ marginTop: '2px' }}>🕒 Slot: <strong>{session.timeSlot || '—'}</strong></div>
                      {session.meetingLink && (
                        <div style={{ marginTop: '4px' }}>🔗 Zoom: <a href={session.meetingLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }} onClick={(e) => e.stopPropagation()}>Join Room</a></div>
                      )}
                    </div>

                    <div className="session-card-footer">
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Auditors:</span>
                      <div className="session-card-auditors">
                        {(session.auditors || []).map(audId => {
                          const aud = auditors?.find(a => String(a.id) === String(audId));
                          const assignmentObj = cohort?.auditorAssignments?.find(a => String(a.auditorId) === String(audId));
                          const isStandby = assignmentObj ? assignmentObj.isStandby : false;

                          return (
                            <span key={audId} className={`auditor-avatar-pill ${isStandby ? 'standby' : ''}`}>
                              {aud ? aud.name : 'Auditor'} {isStandby ? '(S)' : ''}
                            </span>
                          );
                        })}
                        {(!session.auditors || session.auditors.length === 0) && (
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>None assigned</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        );
      })()}

      {/* ---- Cohort Builder (full-page overlay) ---- */}
      {showCohortBuilder && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 500 }}>
          <CohortBuilder
            school={school}
            allStudents={allStudents}
            initialCohorts={schoolCohorts}
            onSave={(updatedCohorts) => {
              onCohortsReplace(updatedCohorts);
              setShowCohortBuilder(false);
            }}
            onCancel={() => setShowCohortBuilder(false)}
          />
        </div>,
        document.body
      )}

      {/* ---- Group create/edit modal ---- */}
      {showGroupForm && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: 'var(--surface)', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '700' }}>{editingGroup ? 'Edit Group' : 'Create Group'}</h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: 'var(--neutral-700)' }}>Group Name <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="text"
                placeholder="e.g. Group Alpha, GSIS A, Welham Group 1"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', fontSize: '14px', border: '1px solid var(--neutral-300)', borderRadius: '8px', outline: 'none', boxSizing: 'border-box', background: 'var(--surface)', color: 'var(--neutral-900)' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: 'var(--neutral-700)' }}>Year</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                {YEAR_OPTIONS.map(y => (
                  <button
                    key={y}
                    onClick={() => setGroupYear(y)}
                    style={{
                      padding: '8px 18px',
                      fontSize: '13px',
                      border: '1px solid',
                      borderColor: groupYear === y ? 'var(--primary-600, #4f46e5)' : 'var(--neutral-300)',
                      background: groupYear === y ? 'var(--primary-600, #4f46e5)' : 'var(--surface)',
                      color: groupYear === y ? '#fff' : 'var(--neutral-700)',
                      borderRadius: '7px',
                      cursor: 'pointer',
                      fontWeight: groupYear === y ? '600' : '400'
                    }}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: 'var(--neutral-700)' }}>
                Select Cohorts
                {groupCohortIds.length > 0 && <span style={{ marginLeft: '8px', fontWeight: '400', color: 'var(--text-muted)' }}>({groupCohortIds.length} selected)</span>}
              </label>
              {schoolCohorts.length === 0 ? (
                <p style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No cohorts yet. Create cohorts first.</p>
              ) : (
                <div style={{ border: '1px solid var(--neutral-200)', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                  {schoolCohorts.map(c => (
                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--neutral-100)', fontSize: '13px' }}>
                      <input
                        type="checkbox"
                        checked={groupCohortIds.includes(c.id)}
                        onChange={() => toggleGroupCohort(c.id)}
                        style={{ accentColor: 'var(--primary-600, #4f46e5)' }}
                      />
                      <span style={{ flex: 1 }}>{c.name}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{(c.studentIds || []).length} students</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowGroupForm(false)} style={{ padding: '9px 20px', fontSize: '13px', background: 'var(--neutral-100)', border: '1px solid var(--neutral-200)', borderRadius: '8px', cursor: 'pointer', color: 'var(--neutral-700)' }}>Cancel</button>
              <button
                onClick={saveGroup}
                disabled={!groupName.trim()}
                style={{ padding: '9px 20px', fontSize: '13px', background: groupName.trim() ? 'var(--primary-600, #4f46e5)' : 'var(--neutral-300)', border: 'none', borderRadius: '8px', cursor: groupName.trim() ? 'pointer' : 'not-allowed', color: '#fff', fontWeight: '600' }}
              >
                {editingGroup ? 'Save Changes' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
